/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface ArquivoData {
  tituloDoArquivo: string;
  urlDoArquivo: string;
}

@Component({
  selector: 'app-arquivo-viewer-dialog',
  templateUrl: 'arquivo-viewer-dialog.component.html',
  styleUrls: ['./arquivo-viewer-dialog.component.css'],
})
export class ArquivoViewerDialogComponent implements OnInit, OnDestroy {
  isFullscreen = false;
  isPdf = false;
  isImage = false;
  isVideo = false;
  pdfSrc?: Blob;
  mediaSrc?: string;

  constructor(
    private el: ElementRef,
    public dialogRef: MatDialogRef<ArquivoViewerDialogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: ArquivoData
  ) {}

  ngOnInit(): void {
    this.checkScreenResolution();
    this.loadFile();
  }

  ngOnDestroy(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    if (this.mediaSrc) URL.revokeObjectURL(this.mediaSrc);
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!document.fullscreenElement;
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      this.el.nativeElement.requestFullscreen().catch(() => {});
    }
  }

  private checkScreenResolution(): void {
    if (window.innerWidth < 1366 || window.innerHeight < 768) {
      this.snackBar.open(
        'Para melhor visualização, recomendamos uma resolução mínima de 1366×768. Use o botão de tela cheia para maximizar.',
        'Entendi',
        { duration: 10000, verticalPosition: 'top' }
      );
    }
  }

  private loadFile(): void {
    const token = localStorage.getItem('access_token') || '';
    if (!token) return;

    fetch(this.data.urlDoArquivo, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.blob(); })
      .then(blob => {
        const mime = blob.type;
        if (mime === 'application/pdf') {
          this.isPdf = true;
          this.pdfSrc = blob;
        } else if (mime.startsWith('image/')) {
          this.isImage = true;
          this.mediaSrc = URL.createObjectURL(blob);
        } else if (mime === 'video/mp4') {
          this.isVideo = true;
          this.mediaSrc = URL.createObjectURL(blob);
        } else {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = this.data.tituloDoArquivo || 'arquivo';
          link.click();
          URL.revokeObjectURL(link.href);
          this.dialogRef.close();
        }
      })
      .catch(err => console.error('Erro ao exibir o arquivo:', err));
  }
}
